import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getEventsByRole,
  createEventRegistration,
} from "../../Utils/apiServices";
import { useAuth } from "../../Context/authContext";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import toast from "../../Utils/toastManager";

const MentorEvents = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registeringId, setRegisteringId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, [user?.role]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      // Use actual role for event visibility: student, mentor, ecosystem_partner, corporate_sponsor, investor
      const role = user?.role || "student";
      const response = await getEventsByRole(role);
      if (response.success && response?.data?.data) {
        const raw = Array.isArray(response.data?.data) ? response.data?.data : [];
        const now = new Date();
        const upcomingOnly = raw.filter((event) => {
          const startAt = event?.start_at ? new Date(event.start_at) : null;
          return startAt && startAt > now;
        });
        setEvents(upcomingOnly);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const formatEventType = (eventType) => {
    if (!eventType) return "";
    return eventType.charAt(0).toUpperCase() + eventType.slice(1);
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const handleRegister = async (eventId) => {
    if (!user) {
      toast.warning("Please login to register for events");
      return;
    }

    try {
      setRegisteringId(eventId);
      const registrationData = {
        event_id: eventId,
        user_id: user.id,
        role: user.role,
        status: "registered",
      };

      const response = await createEventRegistration(registrationData);
      if (response.success) {
        toast.success("Successfully registered for the event!");
      } else {
        toast.error(response.error || "Failed to register for the event");
      }
    } catch (error) {
      console.error("Error registering for event:", error);
      toast.error(error.error || "An error occurred while registering");
    } finally {
      setRegisteringId(null);
    }
  };

  const handleReadMore = (eventId) => {
    if (expandedId === eventId) {
      setExpandedId(null);
    } else {
      setExpandedId(eventId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading events...
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-start justify-between w-full">
        <div className="flex flex-col gap-2 items-start justify-start">
          <Typography as="h2" className="text-2xl font-medium text-primary-900">
            Events
          </Typography>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="p-12 text-center">
          <Typography as="p" className="text-tertiary-600">
            No events available at the moment.
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col gap-6 px-6">
          {events.map((event) => {
            const description = stripHtmlTags(event.description || "");
            const isExpanded = expandedId === event.id;

            return (
              <div
                key={event.id}
                className="w-full flex flex-col items-start justify-start bg-gradient-to-r from-white from-70% to-[#E0F6FF]/30 to-90% rounded-xl py-4 px-8 border border-neutral-200 hover:shadow-md transition-shadow gap-6"
              >
                <div className="w-full flex flex-col gap-4 ">
                  <div className="w-full flex items-center justify-between gap-2">
                    <div className="flex flex-col gap-2 items-start justify-start">
                      {/* Date and Type Tags */}
                      <div className="flex items-center gap-2">
                        {event.start_at && (
                          <span className="px-2 py-1.5 bg-neutral-100 text-stakeholders border border-stakeholders rounded-full text-xs font-medium">
                            {formatDate(event.start_at)}
                          </span>
                        )}
                        {event.event_type && (
                          <span className="px-2 py-1.5 bg-neutral-100 text-stakeholders border border-stakeholders rounded-full text-xs font-medium capitalize">
                            {formatEventType(event.event_type)}
                          </span>
                        )}
                      </div>
                      {/* Event Title */}
                      <Typography
                        as="h3"
                        className="text-lg font-medium text-neutral-1100"
                      >
                        {event.title || "Untitled Event"}
                      </Typography>

                      {/* Event Description */}
                      <Typography
                        as="p"
                        className="text-sm text-neutral-700 text-start"
                      >
                        {description
                          ? isExpanded
                            ? description
                            : description.length > 150
                            ? description.substring(0, 150) + "..."
                            : description
                          : ""}
                      </Typography>

                      {/* Read More Link and Register Button */}
                      <div className="flex items-center justify-between pt-2">
                        <div>
                          {description && description.length > 150 && (
                            <button
                              onClick={() => handleReadMore(event.id)}
                              className="text-[#007FD0]  text-xs font-medium underline"
                            >
                              {isExpanded ? "Read Less" : "Read More"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRegister(event.id)}
                      disabled={registeringId === event.id}
                      variant="primary"
                      className="!bg-[#0D3B5A] hover:bg-brand-blue-hover !text-white !text-lg !font-bold"
                    >
                      {registeringId === event.id
                        ? "Registering..."
                        : "Register"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MentorEvents;
